import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
  AuthCallbackQuerySchema,
  AuthStatusResponseSchema,
  AuthUrlQuerySchema,
  AuthUrlResponseSchema,
} from '../schemas/auth'
import {
  RecordTermsAcceptanceRequestSchema,
  RecordTermsAcceptanceResponseSchema,
  TermsAcceptanceStatusResponseSchema,
} from '../schemas/terms'
import { defaultErrorResponses } from './errors'

const c = initContract()

/**
 * Authentication Contract - Google OAuth 2.0 flow with token management.
 * Stores oauth_tokens in Supabase table with auto-refresh logic (5min before expiry).
 * Callback handler exchanges code for tokens and establishes Supabase session.
 */
export const authContract = c.router({
  /**
   * Initiates Google OAuth flow. Returns auth URL that redirects to callback endpoint.
   * Gets: explicit connect or reconnect intent.
   * Returns: AuthUrlResponseSchema with authUrl (Google OAuth consent screen URL).
   * Side Effects: None (read-only). Generates random state for CSRF protection.
   */
  getAuthUrl: {
    method: 'GET',
    path: '/v1/auth/google',
    query: AuthUrlQuerySchema,
    responses: {
      200: AuthUrlResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Start Google OAuth and return auth URL',
      description: 'Generates a signed, intent-aware Google OAuth URL for connect or reconnect.',
    },
  },
  /**
   * Handles Google OAuth callback. Exchanges auth code for tokens and stores them.
   * Gets: code, state from query params.
   * Returns: 302 redirect to frontend (success or error state).
   * Side Effects: Exchanges code for access_token/refresh_token, stores in oauth_tokens table, sets Supabase session.
   * Error Codes: Invalid code, state mismatch, token exchange failure.
   */
  callback: {
    method: 'GET',
    path: '/v1/auth/callback',
    query: AuthCallbackQuerySchema,
    responses: {
      302: z.object({ redirect: z.string().url() }),
      ...defaultErrorResponses,
    },
    metadata: {
      auth: false,
      summary: 'OAuth callback redirect handler',
      description: 'Exchanges OAuth code for tokens and redirects to frontend with session.',
    },
  },
  /**
   * Disconnects user by invalidating Gmail tokens and removing stored credentials.
   * Gets: auth header (user_id from JWT).
   * Returns: { success: true }.
   * Side Effects: Deletes oauth_tokens row; revokes Gmail token via Google API; clears Supabase session.
   */
  disconnect: {
    method: 'POST',
    path: '/v1/auth/disconnect',
    body: z.object({}),
    responses: {
      200: z.object({ success: z.literal(true) }),
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Disconnect Google OAuth tokens',
      description: 'Revoke Gmail tokens and remove stored credentials.',
    },
  },
  /**
   * Returns Gmail connection status, email, and token health without exposing tokens.
   * Gets: auth header (user_id from JWT).
   * Returns: { gmailEmail, isConnected, tokenHealthy, refreshTokenExpiresAt }.
   * Side Effects: None (read-only). Safe to call from frontend.
   */
  getGmailOauthStatus: {
    method: 'GET',
    path: '/v1/auth/status',
    responses: {
      200: AuthStatusResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Get Gmail connection and token health status',
      description: 'Returns whether Gmail is connected, token health, and refresh token expiry (never returns tokens).',
    },
  },
  /**
   * Records the authenticated user's acceptance of the current Terms of
   * Service and Privacy Policy. Idempotent per (user_id, terms_version):
   * calling it again for the same version returns the existing record with
   * `created: false` instead of inserting a duplicate.
   *
   * Gets: { method, clickwrap? } in body; user_id derived from JWT; IP + UA
   * captured from request headers server-side.
   * Returns: { termsVersion, privacyVersion, acceptedAt, created }.
   * Side Effects: Inserts a row into `user_terms_acceptance` (or no-ops).
   * Emits a Logger.info event for Better Stack observability.
   */
  recordTermsAcceptance: {
    method: 'POST',
    path: '/v1/auth/terms-acceptance',
    body: RecordTermsAcceptanceRequestSchema,
    responses: {
      200: RecordTermsAcceptanceResponseSchema,
      201: RecordTermsAcceptanceResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Record user acceptance of current Terms + Privacy',
      description: 'Evidentiary log of clickwrap / sign-in-wrap assent. Idempotent per (user_id, terms_version).',
    },
  },
  /**
   * Returns whether the authenticated user has accepted the current Terms +
   * Privacy versions. Consumed by the `<TermsReconsentGate>` component on the
   * frontend to decide whether to render a blocking re-consent modal.
   *
   * Gets: user_id derived from JWT.
   * Returns: { needsReconsent, currentTermsVersion, currentPrivacyVersion,
   *   acceptedTermsVersion, acceptedPrivacyVersion, acceptedAt }.
   * Side Effects: None (read-only).
   */
  getTermsAcceptanceStatus: {
    method: 'GET',
    path: '/v1/auth/terms-acceptance-status',
    responses: {
      200: TermsAcceptanceStatusResponseSchema,
      ...defaultErrorResponses,
    },
    metadata: {
      auth: true,
      summary: 'Check whether the user needs to re-accept the current Terms',
      description: 'Compares latest user acceptance to CURRENT_TERMS_VERSION / CURRENT_PRIVACY_VERSION.',
    },
  },
})
